import {
  RequestHeaders,
  APIFetch,
  APIFetchV2,
  JSONToQueryParams,
  APIFetchV3
} from "@certa/network";
import {
  stepGroupModelCreator,
  workflowListModelCreator,
  taskDetailsModelCreator,
  stepUsersModelCreator,
  reportWorkflowsModelCreator,
  fieldHierarchyModelCreator,
  workflowsItemModelCreator,
  relatedKindModalCreator,
  workflowDetailsCreator,
  taskMigrationStatsModelCreator,
  getWorkflowListModelCreator,
  getThirdPartyUsersModelCreator,
  type GetThirdPartyUserResponse,
  delegatedByMeStepUsersModelCreator
} from "../models/workflow.model";
import type {
  GetAdvancedFilterDataORMType,
  GetAdvancedFilterDataORMReturnType,
  CreateWorkflowServiceParams,
  SearchUserWorkflowServiceParams,
  UpdateChildWorkflowServiceParams,
  CreateWorkflowResponse,
  WorkflowFiltersQuery,
  WorkflowItemDetailsResponse,
  ReportExportParams,
  ReportOrdering,
  WorkflowPermissionsType,
  ReportExportORMParams,
  ReportWorkflowsORM,
  MigrationStatsResponse,
  StepUserTagsWorkflowResponse,
  ReportWorkflowsResponseORM,
  GetWorkflowListResponse
} from "../types/workflow.types";
import type { GetORMReportWorkflowsParams } from "../types/reportChartData.types";
import queryString from "query-string";
import { refactorFieldTagsForBE } from "../utils/fields.utils";
import type { WorkflowDynamicAPIResultType } from "@certa/types/src/common";
import {
  ormDynamicAPIDataModelCreator,
  ormReportWorkflowsModelCreator
} from "../models/workflowORM.model";
import { readonlyTableServices } from "./readonlyTable.services";
import type { ExportEmailDeliveryReportParams } from "../types/emailDeliveryReport.types";
import type { ExportIntegrationCallReportParams } from "../types/integrationCallReport.types";
import type {
  WorkflowReportORMDynamicAPIDataPayload,
  WorkflowReportORMDynamicAPIResponse,
  WorkflowReportORMDynamicDataRecord
} from "../types/dynamicAPI.types";
import { getWorkflowUniqKey } from "../utils/workflow.utils";
import { queryClient } from "../utils/utils";
import {
  INVALID_VALUE_IN_DATABASE_ERROR,
  SOMETHING_WENT_WRONG_ERROR
} from "../constants/ormReportWorkflow.constants";
import type { FieldMap } from "@certa/common/utils/fieldHierarchy";
import { getCustomAttributeTag } from "@certa/common/utils/report";
import { INDEX_ZERO } from "@certa/common/constants";
import type {
  UseQueryExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";
import type {
  AggregateResponse,
  ProcessCycleChartAPIResponseType
} from "@certa/common/modelCreators";

const DEFAULT_OFFSET = 0;

/**
 * Creates a child workflow of the specified kind under the specified workflow
 * parent ID provided to it.
 * @param {CreateWorkflowServiceParams} params
 * @returns {Promise<Workflow>} array of model is returned if the API is successful.
 */
function createWorkflow(payload: CreateWorkflowServiceParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload),
    signal: payload?.signal
  };

  return APIFetch<CreateWorkflowResponse>("workflows/", requestOptions);
}

/**
 * Creates a report workflow and updates the column label mappings
 * @param {CreateWorkflowServiceParams} params
 * @returns {Promise<Workflow>} created workflow data is returned if the API is successful.
 */
const createReportWorkFlowWithLabelMappings = async (
  payload: CreateWorkflowServiceParams & {
    labelMappings?: Record<string, string>;
    updateKey?: string;
  }
) => {
  const { labelMappings, updateKey, ...rest } = payload;

  const workflowResponse = await createWorkflow(rest);

  if (labelMappings && updateKey) {
    const reportId = workflowResponse?.id;
    const kindId = workflowResponse?.definition?.kind;
    const dashboardId = workflowResponse?.parents?.[INDEX_ZERO];

    if (kindId && dashboardId) {
      const reports = await readonlyTableServices.getChildWorkflows({
        kind: kindId,
        parents: dashboardId,
        field_tags: [updateKey]
      });

      const report = reports?.find(
        (report: { id: number }) => report.id === reportId
      );

      const updateFieldInfo = report?.fields[updateKey];

      const requestOptions: RequestInit = {
        method: "POST",
        headers: RequestHeaders.POST,
        credentials: "include",
        body: JSON.stringify({
          extra_json: labelMappings,
          field: updateFieldInfo?.id,
          answer: updateFieldInfo?.answer
        })
      };

      await APIFetchV2("responses/", requestOptions);
    }
  }

  return workflowResponse;
};

/**
 * Updates parents' of the given workflow.
 * @param {UpdateChildWorkflowServiceParams} params
 * @returns {Promise} resolves if update is successful, otherwise rejected
 */
function updateChildWorkflow(params: UpdateChildWorkflowServiceParams) {
  const { id, ...payload } = params;
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetch(`workflows/${id}/`, requestOptions);
}

/**
 * Find workflow ID of user workflow associated with the provided email address
 * @param {SearchUserWorkflowServiceParams} params
 * @returns {Promise<number>} resolves to the workflow ID of the workflow found
 * against the provided email
 */
function getUserWorkflowByEmail(params: SearchUserWorkflowServiceParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(params)
  };

  return APIFetch(`workflows/search-user-workflow/`, requestOptions).then(
    (response: any) => response.workflow as number
  );
}

function archiveWorkflow(workflowId: number | undefined) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  const deleteUrl = `workflows/${workflowId}/archive/`;
  return APIFetch(deleteUrl, requestOptions);
}

/**
 * Delinks a child workflow from a parent workflow.
 * Can also remove relationship between associated objects if all object reference fields are provided.
 */
function delink(payload: DelinkPayload) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchV3("linking/delink/", requestOptions);
}

function exportReport(params: ReportExportParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(params)
  };

  const exportUrl = "workflow-reports/export/";
  return APIFetchV2(exportUrl, requestOptions);
}

const exportReportORM = (params: ReportExportORMParams) => {
  const { reportId, filters, allFieldsExport: isAllFieldsExport } = params;
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: filters ? JSON.stringify({ filters }) : undefined
  };

  const exportUrl = isAllFieldsExport
    ? `workflow-reports/${reportId}/export-all-fields/`
    : `workflow-reports/${reportId}/export/`;
  return APIFetchV3(exportUrl, requestOptions);
};

/**
 * Hard deletes a workflow and its child workflows
 * Throw 400 if following rules are not met
 * 1. Workflow should be created by the user discarding it. Not necessarily the child workflows
 * 2. The workflow was created in the last 10 mins
 * 3. First accessible step is not submitted
 * 4. User discarding it, should have the write access to it
 */

type DiscardWorkflowParams = {
  workflowId: number;
  config?: RequestInit;
};

function discardWorkflow({ workflowId, config }: DiscardWorkflowParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    ...config
  };

  return APIFetchV2(`workflows/${workflowId}/discard/`, requestOptions);
}

function getStepGroupList(
  workflowId: number | undefined,
  params?: Record<string, string | number>
) {
  if (!workflowId) {
    return Promise.reject("Workflow ID must be a number");
  }

  const urlParams = queryString.stringify(params || {});
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const paramString = urlParams ? `?${urlParams}` : "";

  return APIFetchV2(
    `workflows/${workflowId}/stepgroups/${paramString}`,
    requestOptions
  ).then(stepGroupModelCreator);
}

const getWorkflowList = (params: any, page = 1) => {
  const urlParams = JSONToQueryParams(params);
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `workflows/?${urlParams}&page=${page}`;
  return APIFetchV2(url, requestOptions)
    .then(workflowListModelCreator)
    .catch(error => {
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        throw new Error(
          `Workflow access denied (${error.status}): Please check your authentication or permissions`
        );
      }
      throw error;
    });
};

const getTaskDetails = (taskId: number) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const detailsURL = `workflows/?id=${taskId}`;
  return APIFetchV2<WorkflowItemDetailsResponse>(detailsURL, requestOptions)
    .then(taskDetailsModelCreator)
    .catch(error => {
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        throw new Error(
          `Task access denied (${error.status}): Cannot access task ${taskId}`
        );
      }
      throw error;
    });
};
const getTaskMigrationStats = ({
  taskId,
  includeTotalEstimatedCompletionTime = false
}: {
  taskId: number | null;
  includeTotalEstimatedCompletionTime?: boolean;
}) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const statsUrl = `migration-stats/?workflow_id=${taskId}`;
  return APIFetchV2<MigrationStatsResponse>(statsUrl, requestOptions)
    .catch(response => response.json())
    .then(taskMigrationStatsModelCreator);
};

// Note: We tried using useInfiniteQuery instead of the below strategy,
// but it did not function as planned and had more complexity.
// See the first commit and comments of the 8963 PR for the useInfiniteQuery approach.
const fetchStepUserTags = async (
  endpoint: string,
  taskId: number,
  modelCreator: (data: StepUserTagsWorkflowResponse) => any
) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const url = `${endpoint}?workflow=${taskId}`;

  const fetchAllPages = async (
    url: string,
    accumulatedData?: StepUserTagsWorkflowResponse
  ): Promise<StepUserTagsWorkflowResponse> => {
    const data = await APIFetch<StepUserTagsWorkflowResponse>(
      url,
      requestOptions
    );

    if (!accumulatedData) {
      accumulatedData = { ...data, results: [...data.results] };
    } else {
      accumulatedData.results = [...accumulatedData.results, ...data.results];
    }
    const page = data.next ? new URL(data.next).searchParams.get("page") : null;
    if (page) {
      return fetchAllPages(`${url}&page=${page}`, accumulatedData);
    }

    return accumulatedData;
  };

  return fetchAllPages(url).then(modelCreator);
};

const getStepUserTags = (taskId: number) =>
  fetchStepUserTags("step-user-tags/", taskId, stepUsersModelCreator);

const getDelegatedToMeStepUserTags = (taskId: number) =>
  fetchStepUserTags(
    "step-user-tags/delegations/",
    taskId,
    stepUsersModelCreator
  );

const getDelegatedByMeStepUserTags = (taskId: number) =>
  fetchStepUserTags(
    "step-user-tags/delegated/",
    taskId,
    delegatedByMeStepUsersModelCreator
  );

// Move this to appropriate file
const getSelectableFieldOptions = (
  kindId: string | undefined,
  fieldTag: string
) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const url = `selectable-field-option?kind_id=${kindId}&field_tag=${fieldTag}`;
  return APIFetchV3(url, requestOptions).then((response: any) => ({
    options: response?.options || []
  }));
};

const getAdvancedFilterData = (
  {
    kindTag,
    processTypeId
  }: {
    kindTag?: string;
    processTypeId?: number;
  },
  config?: Omit<RequestInit, "method" | "headers">
) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include",
    ...config
  };

  const queryParams = JSONToQueryParams({
    active_kind: kindTag || "",
    active_kind_id: processTypeId || ""
  });

  const url = `fields/export-json/?${queryParams}`;
  return APIFetch(url, requestOptions).then(data =>
    fieldHierarchyModelCreator(data)
  );
};

const getAdvancedFilterDataORM = (
  {
    processTypeId,
    childrenKindIds = [],
    grandChildrenKindIds = [],
    isORMReport
  }: GetAdvancedFilterDataORMType,
  config?: Omit<RequestInit, "method" | "headers">
): Promise<GetAdvancedFilterDataORMReturnType> => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include",
    ...config
  };

  const recursiveFunction = async (id: string) => {
    const url = `fields/export-json/?active_kind_id=${id}`;
    return await APIFetch(url, requestOptions).then(data => ({
      kindId: id,
      advancedFilterData: fieldHierarchyModelCreator(data)
    }));
  };

  const url = `fields/export-json/?active_kind_id=${processTypeId}`;
  return APIFetch(url, requestOptions).then(async data => {
    const parentOption = fieldHierarchyModelCreator(data);
    const childrenOptions = childrenKindIds.map(id => recursiveFunction(id));
    const grandChildrenOptions = grandChildrenKindIds.map(id =>
      recursiveFunction(id)
    );
    const children = await Promise.all(childrenOptions);
    const grandChildren = await Promise.all(grandChildrenOptions);
    const mapping = getMapping({
      processTypeId,
      parentOption,
      children,
      grandChildren,
      isORMReport
    });
    return { parentOption, children, grandChildren, ...mapping };
  });
};

type AdvancedFilterDataORMChild = {
  kindId: string;
  advancedFilterData: ReturnType<typeof fieldHierarchyModelCreator>;
};

type GetMappingType = {
  processTypeId: number | undefined;
  parentOption: ReturnType<typeof fieldHierarchyModelCreator>;
  children: AdvancedFilterDataORMChild[];
  grandChildren: AdvancedFilterDataORMChild[];
  isORMReport: boolean;
};
const getMapping = ({
  processTypeId,
  parentOption,
  children,
  grandChildren,
  isORMReport
}: GetMappingType) => {
  if (!isORMReport)
    return {
      fieldTagMapping: { ...parentOption.mapping },
      stepGroupAndStepMapping: { ...parentOption.stepGroupAndStepMapping }
    };
  const fieldTagMapping: FieldMap = {
    ...getMappingHelper(parentOption.mapping, processTypeId?.toString())
  };
  const stepGroupAndStepMapping = {
    ...getMappingHelper(
      parentOption.stepGroupAndStepMapping,
      processTypeId?.toString()
    )
  };
  children.forEach(({ kindId, advancedFilterData }) => {
    Object.assign(
      fieldTagMapping,
      getMappingHelper(advancedFilterData.mapping, kindId)
    );
    Object.assign(
      stepGroupAndStepMapping,
      getMappingHelper(advancedFilterData.stepGroupAndStepMapping, kindId)
    );
  });
  grandChildren.forEach(({ kindId, advancedFilterData }) => {
    Object.assign(
      fieldTagMapping,
      getMappingHelper(advancedFilterData.mapping, kindId)
    );
    Object.assign(
      stepGroupAndStepMapping,
      getMappingHelper(advancedFilterData.stepGroupAndStepMapping, kindId)
    );
  });
  return { fieldTagMapping, stepGroupAndStepMapping };
};

const getMappingHelper = <T>(
  mapping: Record<string, T> | undefined,
  kindId: string | undefined
) => {
  const fieldMapping = {} as Record<string, T>;
  if (!mapping) return fieldMapping;
  Object.keys(mapping).forEach(key => {
    fieldMapping[getCustomAttributeTag(kindId, key)] = mapping[key];
  });
  return fieldMapping;
};

const getReportWorkflowsCount = (
  filters: WorkflowFiltersQuery,
  config?: Omit<RequestInit, "method" | "headers" | "body">
) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      filters
    }),
    ...config
  };
  return APIFetchV2(`workflow-reports/count/`, requestOptions);
};

const getReportWorkflows = (
  filters: WorkflowFiltersQuery & { offset: number },
  fieldTags: string[],
  ordering?: ReportOrdering[],
  config?: Omit<RequestInit, "method" | "headers" | "body">,
  customPageSize?: number
) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      ...refactorFieldTagsForBE(fieldTags),
      filters,
      ordering
    }),
    ...config
  };
  return APIFetchV2(
    `workflow-reports/?offset=${filters.offset}${customPageSize ? `&limit=${customPageSize}` : ""}&count=false`,
    requestOptions
  ).then(reportWorkflowsModelCreator);
};

const getORMReportWorkflows = ({
  filters,
  attributes = [],
  orderBy = [],
  operations = [],
  groupBy = [],
  convertData,
  limit,
  extraData,
  config
}: GetORMReportWorkflowsParams): Promise<
  ReportWorkflowsORM | AggregateResponse
> => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      attributes,
      filters,
      operations,
      group_by: groupBy,
      order_by: orderBy,
      extra_data: extraData
    }),
    ...config
  };
  return APIFetchV3<ReportWorkflowsResponseORM | AggregateResponse>(
    `workflow-reports/?offset=${filters?.offset ?? DEFAULT_OFFSET}${
      limit ? "&limit=" + limit : ""
    }`,
    requestOptions
  )
    .then(async data =>
      convertData ? ormReportWorkflowsModelCreator(data, attributes) : data
    )
    .catch(async (error: Response) => {
      let body: unknown | null = null;
      try {
        body = await error.json();
      } catch (error) {
        // Do nothing
      }
      if (Array.isArray(body)) {
        if (
          body.includes(
            "Invalid value in the database for this query. Please contact customer care."
          )
        ) {
          throw new Error(INVALID_VALUE_IN_DATABASE_ERROR);
        }
      }
      throw new Error(SOMETHING_WENT_WRONG_ERROR);
    });
};

const getORMWorkflowInfoDynamicAPI = (
  data: WorkflowReportORMDynamicAPIDataPayload,
  ids: number[]
): Promise<WorkflowReportORMDynamicDataRecord> => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      data,
      filters: {
        id__in: ids?.join(",")
      }
    })
  };
  return APIFetchV3<WorkflowReportORMDynamicAPIResponse>(
    `workflows-list-drest/?limit=none`,
    requestOptions
  ).then(ormDynamicAPIDataModelCreator);
};

const getWorkflowsList = (
  payload: any,
  offset: number = 0,
  limit: number = 20
) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchV3<GetWorkflowListResponse>(
    `workflows-list-drest/?limit=${limit}&offset=${offset}`,
    requestOptions
  ).then(getWorkflowListModelCreator);
};

const exportEmailDeliveryReport = ({
  attributes,
  reportId,
  filters
}: ExportEmailDeliveryReportParams) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      attributes,
      filters
    })
  };

  const exportUrl = `sendgrid-email-status-reports/${reportId}/export/`;
  return APIFetchV3(exportUrl, requestOptions);
};

function exportIntegrationReport({
  attributes,
  reportId,
  filters
}: ExportIntegrationCallReportParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      attributes,
      filters
    })
  };

  const exportUrl = `api-call-logs-reports/${reportId}/export/`;
  return APIFetchV3(exportUrl, requestOptions);
}

const getApiCallLogs = (queryUrlParams: string, offset: number) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `api-call-logs/${queryUrlParams}${
      queryUrlParams ? "&" : "?"
    }limit=20&offset=${offset}`,
    requestOptions
  );
};

const getEmailDeliveryReports = (queryUrlParams: string, offset: number) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `email-delivery-reports/${queryUrlParams}${
      queryUrlParams ? "&" : "?"
    }limit=20&offset=${offset}`,
    requestOptions
  );
};
const getEmailDeliveryReportsStatuses = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `email-delivery-reports/distinct/status/
   `,
    requestOptions
  );
};

const getEmailDeliveryReportsSubjects = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `email-delivery-reports/distinct/subject/
   `,
    requestOptions
  );
};

const getEmailDeliveryReportsSmtpCodes = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `email-delivery-reports/distinct/smtp_status_code/
   `,
    requestOptions
  );
};

const getEmailDeliveryReportsKindIds = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `email-delivery-reports/distinct/wf_kind/
   `,
    requestOptions
  );
};

const getGetIntegrationReportKindIds = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `api-call-logs/distinct/kind_id/
   `,
    requestOptions
  );
};

const getGetIntegrationReportStatues = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `api-call-logs/distinct/status/
   `,
    requestOptions
  );
};

const getGetIntegrationReportApiTypes = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2(
    `api-call-logs/distinct/api_type/
   `,
    requestOptions
  );
};

function getWorkflowDetailsById(id: number | undefined) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2<WorkflowItemDetailsResponse>(
    `workflows/?id=${id}`,
    requestOptions
  ).then(workflowsItemModelCreator);
}

function geWorkflowPermissionsTypeById(id: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchV2<WorkflowPermissionsType>(
    `workflow-lvl-perms/${id}/`,
    requestOptions
  );
}

function dynamicWorkflowAPI<T>(body: T) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(body)
  };

  return APIFetchV2<WorkflowDynamicAPIResultType<T>>(
    `workflows-ql/`,
    requestOptions
  ).then(workflowDetailsCreator);
}

const getRelatedWorkflows = (kindId: number) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.POST,
    credentials: "include"
  };
  return APIFetchV2<ProcessCycleChartAPIResponseType>(
    `related-workflow-kinds/?kind=${kindId}`,
    requestOptions
  ).then(data => relatedKindModalCreator(data));
};

const lazyFetchProcessDetails = async (
  workflowId: number,
  config?: UseQueryExtraConfigType
) =>
  await queryClient.fetchQuery<UseQueryReturnType<typeof getTaskDetails>>(
    getWorkflowUniqKey(workflowId),
    () => getTaskDetails(workflowId),
    {
      retry: 0,
      ...config
    }
  );

const getThirdPartyUsers = (
  processId: number,
  thirdPartyUserDefinitionTag?: string
) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const queryParam = thirdPartyUserDefinitionTag
    ? `?swimlane_tag=${thirdPartyUserDefinitionTag}`
    : "";
  return APIFetchV3<GetThirdPartyUserResponse>(
    `workflows/${processId}/third-party-users/${queryParam}`,
    requestOptions
  ).then(getThirdPartyUsersModelCreator);
};

export type DelinkPayload = {
  parent_workflow_id: number;
  child_workflow_id: number;
  parent_object_reference?: string;
  child_object_reference?: string;
  relation_name?: string;
};

export type InviteThirdPartyUserPayload = {
  processId: number;
  user: string; // This is the email of the user.
  first_name?: string;
  last_name?: string;
  group: string;
  language?: string;
};

const inviteThirdPartyUser = (payload: InviteThirdPartyUserPayload) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchV3<GetThirdPartyUserResponse[number]>(
    `workflows/${payload.processId}/third-party-users/`,
    requestOptions
  );
};

type DeleteThirdPartyUserPayload = {
  processId: number;
  email: string;
  group: string;
};

const deleteThirdPartyUser = (payload: DeleteThirdPartyUserPayload) => {
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.DELETE,
    credentials: "include",
    body: JSON.stringify({
      user: payload.email,
      group: payload.group
    })
  };
  return APIFetchV3(
    `workflows/${payload.processId}/third-party-users/`,
    requestOptions
  );
};

export const workflowServices = {
  createWorkflow,
  createReportWorkFlowWithLabelMappings,
  archiveWorkflow,
  delink,
  updateChildWorkflow,
  getUserWorkflowByEmail,
  getStepGroupList,
  getWorkflowList,
  getTaskDetails,
  discardWorkflow,
  getStepUserTags,
  getDelegatedToMeStepUserTags,
  getDelegatedByMeStepUserTags,
  getAdvancedFilterData,
  getAdvancedFilterDataORM,
  getReportWorkflowsCount,
  getReportWorkflows,
  getORMReportWorkflows,
  getORMWorkflowInfoDynamicAPI,
  getWorkflowsList,
  exportReport,
  exportReportORM,
  getWorkflowDetailsById,
  geWorkflowPermissionsTypeById,
  dynamicWorkflowAPI,
  getRelatedWorkflows,
  getApiCallLogs,
  getEmailDeliveryReports,
  getEmailDeliveryReportsStatuses,
  getEmailDeliveryReportsSubjects,
  getEmailDeliveryReportsSmtpCodes,
  getEmailDeliveryReportsKindIds,
  getGetIntegrationReportKindIds,
  getGetIntegrationReportApiTypes,
  getGetIntegrationReportStatues,
  exportEmailDeliveryReport,
  exportIntegrationReport,
  getSelectableFieldOptions,
  getTaskMigrationStats,
  lazyFetchProcessDetails,
  getThirdPartyUsers,
  inviteThirdPartyUser,
  deleteThirdPartyUser
};
